import api from './api';

// The server builds every .xlsx - it is the side that holds the data, and doing
// it there keeps one description of the columns instead of two that can drift.
// This file only moves files in and out of the browser.

// Hand the finished file to the browser. The object URL is released straight
// away: leaving it alive pins the whole workbook in memory for the life of the
// tab, and a full backup is not small.
const saveBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// The server names the file in Content-Disposition; fall back to our own name if
// the header did not survive (a proxy that strips it, say).
const filenameFrom = (response, fallback) => {
    const header = response.headers?.['content-disposition'] || '';
    const match = /filename="?([^"';]+)"?/i.exec(header);
    return match ? match[1] : fallback;
};

// An error response to a blob request arrives as a Blob too, so the server's
// message has to be read back out of it rather than shown as "[object Blob]".
const messageFromBlobError = async (error) => {
    const data = error.response?.data;
    if (!(data instanceof Blob)) return error.response?.data?.message || error.message;

    try {
        return JSON.parse(await data.text()).message || error.message;
    } catch (parseError) {
        return error.message;
    }
};

const download = async (url, fallbackName) => {
    try {
        const response = await api.get(url, { responseType: 'blob' });
        saveBlob(response.data, filenameFrom(response, fallbackName));
    } catch (error) {
        throw new Error(await messageFromBlobError(error));
    }
};

const dataService = {
    // What can be transferred, and the columns of each section.
    getSections: async () => {
        const response = await api.get('/data/sections');
        return response.data;
    },

    exportSection: (section) => download(`/data/export/${section}`, `${section}.xlsx`),

    // An empty sheet with the right headers, for records typed up from scratch.
    downloadTemplate: (section) => download(`/data/template/${section}`, `${section}-template.xlsx`),

    // Everything this doctor has, one sheet per section.
    downloadBackup: () => download('/data/backup', 'backup.xlsx'),

    importSection: async (section, file) => {
        const form = new FormData();
        form.append('file', file);

        const response = await api.post(`/data/import/${section}`, form, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    },

    // Read a whole backup workbook back in, every sheet it recognises.
    importBackup: async (file) => {
        const form = new FormData();
        form.append('file', file);

        const response = await api.post('/data/import', form, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
        return response.data;
    }
};

export default dataService;
